/**
 * Márgenes de las hojas en el editor de Revisión (Opciones 2 y 3, manuscritos
 * y escaneados), como Disposición → Márgenes de Word. Pedido de Andrey (14 oct
 * 2026): «poder editar el margen, poder ajustarlo en el HTML así como en Word».
 *
 * Contrato con la exportación (agente B, CONTRATO.md):
 *  - Cada `.manuscript-page`, también las hojas extra de la Opción 2, lleva
 *    `data-ez-margenes="SUP DER INF IZQ"` en mm (orden del padding de CSS,
 *    hasta 2 decimales, punto decimal) y, en línea, `--mpad-l/r/t/b` en px.
 *    El Word y el PDF leen el atributo.
 *  - Sin `padding` en línea: x2t (HTML → Word) lo aplicaba además del pgMar y
 *    estrechaba las tablas (una tabla de 3 columnas salía con una letra por
 *    renglón y el Word con una hoja de más). El relleno que ya traía la hoja
 *    se deja como estaba.
 *  - `--mpad-l/r` son los laterales que ya leían el editor y el PDF nativo
 *    (html-pdf.service), así que el PDF sigue los laterales nuevos sin más.
 *  - `data-margen-izq-pt` / `data-margen-der-pt` son lo medido del original y
 *    no se tocan: de ahí sale «Como el original».
 *  - Encabezado y pie del documento (doc-header/doc-footer): viven DENTRO del
 *    margen, como en Word. El texto empieza a max(SUP, alto de la banda).
 *
 * Prioridad en el editor, la misma que en el export: los `data-ez-margenes`
 * del documento > el margen por defecto de la CUENTA (Ajustes → Documento
 * traducido) > lo de hoy. El de la cuenta no se escribe en el documento: va en
 * el body del editor (variables y un atributo que no se guardan), así un
 * documento con márgenes guardados nunca cambia al abrirlo y la preferencia
 * se aplica en vivo sin volver a montar el editor.
 *
 * Lo que va anclado a la hoja (la imagen de fondo `page-bg`, las imágenes
 * `data-ez-fondo` del mix, en cm desde la esquina) se posiciona contra la caja
 * de la hoja y no contra su relleno: los márgenes no lo mueven ni lo cortan.
 *
 * Archivo sin React para poder probarlo con vitest.
 */

export const ATRIBUTO_MARGENES = 'data-ez-margenes';
export const BOTON_MARGENES = 'ezmargenes';
/** En el body del editor (no se guarda): el margen por defecto de la cuenta, «SUP DER INF IZQ» en mm. */
export const ATRIBUTO_MARGEN_CUENTA = 'data-ez-margen-cuenta';
/** En el body del editor (no se guarda): el encabezado y el pie van al ancho del texto. */
export const ATRIBUTO_BANDAS = 'data-ez-bandas-al-texto';

/** Márgenes en mm. */
export interface Margenes {
  sup: number;
  der: number;
  inf: number;
  izq: number;
}

export type CampoMargen = keyof Margenes;
export const CAMPOS_MARGEN: readonly CampoMargen[] = ['sup', 'inf', 'izq', 'der'];

export type IdPreset = 'normal' | 'estrecho' | 'moderado' | 'ancho';

/** Los de Word, en pulgadas exactas: 1, 0,5, 1 × 0,75 y 1 × 2. */
export const PRESETS_MARGENES: ReadonlyArray<{ id: IdPreset; titulo: string; margenes: Margenes }> = [
  { id: 'normal', titulo: 'Normal', margenes: { sup: 25.4, der: 25.4, inf: 25.4, izq: 25.4 } },
  { id: 'estrecho', titulo: 'Narrow', margenes: { sup: 12.7, der: 12.7, inf: 12.7, izq: 12.7 } },
  { id: 'moderado', titulo: 'Moderate', margenes: { sup: 25.4, der: 19.05, inf: 25.4, izq: 19.05 } },
  { id: 'ancho', titulo: 'Wide', margenes: { sup: 25.4, der: 50.8, inf: 25.4, izq: 50.8 } },
];

export const TEXTO_ORIGINAL = 'As in the original';
export const TEXTO_PERSONALIZADO = 'Custom margins...';
export const TEXTO_BOTON = 'Margins';
/** Último del menú: mostrar u ocultar la regla de márgenes (regla-margenes). */
export const TEXTO_MOSTRAR_REGLA = 'Show ruler';

export const MARGEN_MIN_MM = 5;
/** 2 in: el «Ancho» de Word tiene que caber en el diálogo. */
export const MARGEN_MAX_MM = 50.8;
/** Lo que queda para el texto, de ancho y de alto. */
export const COLUMNA_MIN_MM = 40;
/** «Como el original» acota lo medido como el export por defecto (contrato con B). */
export const ORIGINAL_MIN_MM = 10;
export const ORIGINAL_MAX_MM = 35;
export const MM_POR_PULGADA = 25.4;
export const PX_POR_MM = 96 / MM_POR_PULGADA;
/** El editor de hoy: 38 px arriba y abajo; a los lados, --mpad-l/r o 40 px. */
export const VERTICAL_HOY_PX = 38;
export const LATERAL_HOY_PX = 40;
/** El redondeo a px enteros de --mpad (hasta 0,13 mm) no cuenta como otro margen. */
export const TOLERANCIA_MM = 0.15;

const r2 = (n: number) => Math.round(n * 100) / 100;
export const mmAPx = (mm: number): number => mm * PX_POR_MM;
export const pxAMm = (px: number): number => px / PX_POR_MM;
const pxEnLinea = (mm: number) => `${r2(mmAPx(mm))}px`;

/** `data-ez-margenes` → mm, o null si no son cuatro números razonables. */
export function leerMargenes(valor: string | null | undefined): Margenes | null {
  const v = String(valor ?? '').trim().split(/\s+/).map(Number);
  if (v.length !== 4 || !v.every((x) => Number.isFinite(x) && x >= 0 && x <= 200)) return null;
  return { sup: v[0], der: v[1], inf: v[2], izq: v[3] };
}

export function textoMargenes(m: Margenes): string {
  return [m.sup, m.der, m.inf, m.izq].map((x) => String(r2(x))).join(' ');
}

export function redondearMargenes(m: Margenes): Margenes {
  return { sup: r2(m.sup), der: r2(m.der), inf: r2(m.inf), izq: r2(m.izq) };
}

export function margenesIguales(a: Margenes | null | undefined, b: Margenes | null | undefined): boolean {
  if (!a || !b) return false;
  return CAMPOS_MARGEN.every((k) => Math.abs(a[k] - b[k]) <= TOLERANCIA_MM);
}

export function presetDe(m: Margenes | null | undefined): IdPreset | null {
  return PRESETS_MARGENES.find((p) => margenesIguales(p.margenes, m))?.id ?? null;
}

// ── Unidades ────────────────────────────────────────────────────────────────

/** Unidad en que se ven y se escriben los márgenes (se guardan siempre en mm). */
export type UnidadMargen = 'mm' | 'cm' | 'in';

const MM_POR: Record<UnidadMargen, number> = { mm: 1, cm: 10, in: MM_POR_PULGADA };
/** Decimales con que se ven y se escriben: cm con 1, in con 2 (como la regla); mm con 2. */
export const decimalesDe = (u: UnidadMargen): number => (u === 'cm' ? 1 : 2);

export interface HojaEditor {
  anchoMm: number;
  altoMm: number;
}

/** Carta y Oficio (215,9 mm de lado corto) en pulgadas, como Word en EE. UU.; lo demás en mm. */
export function unidadDeHoja(hoja: HojaEditor | null | undefined): UnidadMargen {
  if (!hoja) return 'mm';
  const corto = Math.min(hoja.anchoMm, hoja.altoMm);
  return Math.abs(corto - 215.9) <= 1.5 ? 'in' : 'mm';
}

export const aUnidad = (mm: number, u: UnidadMargen): number => mm / MM_POR[u];
export const deUnidad = (v: number, u: UnidadMargen): number => v * MM_POR[u];

/**
 * Lo escrito en la unidad → mm para guardar: primero a la precisión de la
 * unidad (cm con 1 decimal, in con 2) y luego a la centésima de mm. Así lo
 * que se escribe o se arrastra vuelve igual al abrir otra vez (sin deriva).
 */
export function mmDeValor(v: number, u: UnidadMargen): number {
  const k = 10 ** decimalesDe(u);
  // El 1e-7 deja «2,55» en 2,6 y no en 2,5 (2,55 × 10 da 25,4999… en coma flotante).
  return Math.round((Math.round(v * k + 1e-7) / k) * MM_POR[u] * 100) / 100;
}

/**
 * Un límite en la unidad, redondeado hacia dentro del rango: el mínimo hacia
 * arriba y el máximo hacia abajo, para que el número que se lee se pueda escribir
 * (50,8 mm en cm es «5», no «5,1»).
 */
export function formatearLimite(mm: number, u: UnidadMargen, locale: string | null | undefined, sentido: 'min' | 'max'): string {
  const k = 10 ** decimalesDe(u);
  const v = aUnidad(mm, u) * k;
  const redondo = (sentido === 'min' ? Math.ceil(v - 1e-9) : Math.floor(v + 1e-9)) / k;
  return formatearValor(deUnidad(redondo, u), u, locale);
}

/** Número en el idioma de la interfaz: 25,4 · 0,75 · 1. */
export function formatearValor(mm: number, u: UnidadMargen, locale?: string | null, decimales = decimalesDe(u)): string {
  const v = aUnidad(mm, u);
  try {
    return new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: decimales, useGrouping: false }).format(v);
  } catch {
    return String(Math.round(v * 10 ** decimales) / 10 ** decimales);
  }
}

/** Lo que se escribe en el diálogo, con coma o punto. null si no es un número. */
export function leerNumero(texto: string | null | undefined): number | null {
  const t = String(texto ?? '').trim().replace(',', '.');
  if (!/^\d+(?:\.\d+)?$|^\.\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** «25,4 mm» o «↕ 25,4 · ↔ 19,1 mm» (arriba/abajo y lados cuando difieren), como el menú de Word: mm con 1 decimal. */
export function resumenMargenes(m: Margenes, u: UnidadMargen, locale?: string | null, etiquetas?: { mm: string; in: string; cm?: string } | null): string {
  const f = (mm: number) => formatearValor(mm, u, locale, u === 'in' ? 2 : 1);
  const unidad = (u === 'in' ? etiquetas?.in : u === 'cm' ? etiquetas?.cm : etiquetas?.mm) || u;
  const igual = (a: number, b: number) => f(a) === f(b);
  if (igual(m.sup, m.inf) && igual(m.sup, m.izq) && igual(m.sup, m.der)) return `${f(m.sup)} ${unidad}`;
  const vertical = igual(m.sup, m.inf) ? f(m.sup) : `${f(m.sup)}/${f(m.inf)}`;
  const lateral = igual(m.izq, m.der) ? f(m.izq) : `${f(m.izq)}/${f(m.der)}`;
  return `↕ ${vertical} · ↔ ${lateral} ${unidad}`;
}

// ── Validación ──────────────────────────────────────────────────────────────

export type ErrorMargen =
  | { campo: CampoMargen; tipo: 'numero' | 'rango' }
  | { campo: 'ancho' | 'alto'; tipo: 'columna' };

/** Errores del diálogo: cada valor entre 5 y 50,8 mm y al menos 40 mm de texto a lo ancho y a lo alto. */
export function validarMargenes(m: Partial<Record<CampoMargen, number | null>>, hoja?: HojaEditor | null): ErrorMargen[] {
  const errores: ErrorMargen[] = [];
  for (const k of CAMPOS_MARGEN) {
    const v = m[k];
    if (v === null || v === undefined || !Number.isFinite(v)) errores.push({ campo: k, tipo: 'numero' });
    else if (v < MARGEN_MIN_MM - 1e-9 || v > MARGEN_MAX_MM + 1e-9) errores.push({ campo: k, tipo: 'rango' });
  }
  if (errores.length || !hoja) return errores;
  const { sup, inf, izq, der } = m as Margenes;
  if (hoja.anchoMm - izq - der < COLUMNA_MIN_MM) errores.push({ campo: 'ancho', tipo: 'columna' });
  if (hoja.altoMm - sup - inf < COLUMNA_MIN_MM) errores.push({ campo: 'alto', tipo: 'columna' });
  return errores;
}

/**
 * Lo del diálogo, en mm: lo escrito (mmDeValor) y, en los campos que no se
 * tocaron, los mm de antes tal cual: 24,87 mm se ve «2,5» en cm y, si no se
 * toca, sigue en 24,87.
 */
export function mmDelDialogo(
  valores: Record<CampoMargen, string>,
  iniciales: Record<CampoMargen, string>,
  actuales: Margenes | null,
  u: UnidadMargen,
): Record<CampoMargen, number | null> {
  const r = {} as Record<CampoMargen, number | null>;
  for (const k of CAMPOS_MARGEN) {
    if (actuales && valores[k] === iniciales[k] && Number.isFinite(actuales[k])) {
      r[k] = actuales[k];
      continue;
    }
    const n = leerNumero(valores[k]);
    r[k] = n === null ? null : mmDeValor(n, u);
  }
  return r;
}

// ── Estilo en línea de la hoja ──────────────────────────────────────────────

/** Declaraciones de un style respetando comillas y paréntesis (un url(data:…;base64,…) lleva «;»). */
export function partirDeclaraciones(estilo: string): string[] {
  const out: string[] = [];
  let actual = '';
  let comilla: string | null = null;
  let parentesis = 0;
  for (const c of String(estilo ?? '')) {
    if (comilla) {
      if (c === comilla) comilla = null;
    } else if (c === '"' || c === "'") {
      comilla = c;
    } else if (c === '(') {
      parentesis++;
    } else if (c === ')') {
      parentesis = Math.max(0, parentesis - 1);
    } else if (c === ';' && parentesis === 0) {
      if (actual.trim()) out.push(actual.trim());
      actual = '';
      continue;
    }
    actual += c;
  }
  if (actual.trim()) out.push(actual.trim());
  return out;
}

const VARIABLES_DEL_MARGEN = /^--mpad-[lrtb]$/i;

/** El style de la hoja con los márgenes nuevos (sus --mpad): lo demás queda igual y en su orden. */
export function estiloConMargenes(estilo: string, m: Margenes): string {
  const resto = partirDeclaraciones(estilo).filter((d) => {
    const i = d.indexOf(':');
    return i <= 0 || !VARIABLES_DEL_MARGEN.test(d.slice(0, i).trim());
  });
  return [
    ...resto,
    `--mpad-l: ${pxEnLinea(m.izq)}`,
    `--mpad-r: ${pxEnLinea(m.der)}`,
    `--mpad-t: ${pxEnLinea(m.sup)}`,
    `--mpad-b: ${pxEnLinea(m.inf)}`,
  ].join('; ') + ';';
}

// ── Hojas del documento ─────────────────────────────────────────────────────

/** Marca de CADA hoja con plantilla (Opción 4): sus márgenes los manda la plantilla. Las mixtas no la llevan. */
const MARCA_PLANTILLA = 'data-ez-plantilla';

const esHojaConMargenes = (el: Element): el is HTMLElement => el.nodeType === 1
  && el.classList.contains('manuscript-page')
  && !el.hasAttribute(MARCA_PLANTILLA);

/** Las hojas que reciben los márgenes: las del cuerpo sin plantilla (con plantilla manda ella). */
export function hojasDelDocumento(body: Element | null | undefined): HTMLElement[] {
  const r: HTMLElement[] = [];
  if (!body) return r;
  for (let c = body.firstElementChild; c; c = c.nextElementSibling) if (esHojaConMargenes(c)) r.push(c);
  return r;
}

export interface DomMargenes {
  setAttrib(el: HTMLElement, nombre: string, valor: string | null): void;
}

const domPorDefecto: DomMargenes = {
  setAttrib: (el, n, v) => (v === null ? el.removeAttribute(n) : el.setAttribute(n, v)),
};

/**
 * Pone los márgenes en todas las hojas y devuelve cuántas cambió. `dom` debe
 * ser `editor.dom` en TinyMCE: su setAttrib('style') también pone
 * data-mce-style, que es lo que se serializa.
 */
export function aplicarMargenes(body: Element | null | undefined, m: Margenes, dom: DomMargenes = domPorDefecto): number {
  const valor = textoMargenes(m);
  let n = 0;
  for (const pg of hojasDelDocumento(body)) {
    const antes = pg.getAttribute('style') || '';
    const estilo = estiloConMargenes(antes, m);
    if (pg.getAttribute(ATRIBUTO_MARGENES) === valor && antes === estilo) continue;
    dom.setAttrib(pg, 'style', estilo);
    dom.setAttrib(pg, ATRIBUTO_MARGENES, valor);
    n++;
  }
  return n;
}

const ptAMm = (pt: number) => (pt * MM_POR_PULGADA) / 72;
const acotarOriginal = (mm: number) => Math.min(ORIGINAL_MAX_MM, Math.max(ORIGINAL_MIN_MM, mm));

/**
 * «Como el original»: los laterales medidos del original (los de la mayoría de
 * las hojas) y el vertical de hoy, acotados a 10-35 mm como el export por
 * defecto. null si el documento no trae medida (escaneados viejos, Word).
 */
export function margenesDelOriginal(body: Element | null | undefined): Margenes | null {
  const cuenta = new Map<string, { izq: number; der: number; n: number }>();
  for (const pg of hojasDelDocumento(body)) {
    const izq = parseFloat(pg.getAttribute('data-margen-izq-pt') || '');
    const der = parseFloat(pg.getAttribute('data-margen-der-pt') || '');
    if (!Number.isFinite(izq) || !Number.isFinite(der) || izq < 0 || der < 0) continue;
    const k = `${izq}|${der}`;
    const e = cuenta.get(k);
    if (e) e.n++;
    else cuenta.set(k, { izq, der, n: 1 });
  }
  let mejor: { izq: number; der: number; n: number } | null = null;
  for (const e of Array.from(cuenta.values())) if (!mejor || e.n > mejor.n) mejor = e;
  if (!mejor) return null;
  const vertical = acotarOriginal(pxAMm(VERTICAL_HOY_PX));
  return redondearMargenes({ sup: vertical, der: acotarOriginal(ptAMm(mejor.der)), inf: vertical, izq: acotarOriginal(ptAMm(mejor.izq)) });
}

/** Lo que se ve de una hoja, en px de layout: su relleno y las bandas que caen en su margen. */
export interface CajaHoja {
  padTop: number;
  padRight: number;
  padBottom: number;
  padLeft: number;
  /** Alto del encabezado y del pie del documento (--hf-top / --hf-bot). */
  bandaSup: number;
  bandaInf: number;
}

/**
 * Los márgenes que tiene hoy el documento: los de `data-ez-margenes` si los
 * trae; si no, el margen por defecto de la cuenta (cuando exista); si no, lo
 * que se ve (relleno más banda, la regla de Word). Sin layout, lo de la
 * etiqueta: --mpad-l/r o 40 px, y 38 px.
 */
export function margenesActuales(
  body: Element | null | undefined,
  opciones: { porDefecto?: Margenes | null; caja?: (pg: HTMLElement) => CajaHoja | null } = {},
): Margenes | null {
  const pg = hojasDelDocumento(body)[0];
  if (!pg) return null;
  const propios = leerMargenes(pg.getAttribute(ATRIBUTO_MARGENES));
  if (propios) return propios;
  if (opciones.porDefecto) return opciones.porDefecto;
  let c: CajaHoja | null = null;
  try {
    c = opciones.caja?.(pg) ?? null;
  } catch {
    c = null;
  }
  if (c && [c.padTop, c.padRight, c.padBottom, c.padLeft].every((v) => Number.isFinite(v) && v >= 0)) {
    return redondearMargenes({
      sup: pxAMm(c.padTop + Math.max(0, c.bandaSup || 0)),
      der: pxAMm(c.padRight),
      inf: pxAMm(c.padBottom + Math.max(0, c.bandaInf || 0)),
      izq: pxAMm(c.padLeft),
    });
  }
  const mpad = (nombre: string) => {
    const v = parseFloat(new RegExp(`${nombre}\\s*:\\s*([\\d.]+)px`, 'i').exec(pg.getAttribute('style') || '')?.[1] ?? '');
    return Number.isFinite(v) ? v : LATERAL_HOY_PX;
  };
  return redondearMargenes({ sup: pxAMm(VERTICAL_HOY_PX), der: pxAMm(mpad('--mpad-r')), inf: pxAMm(VERTICAL_HOY_PX), izq: pxAMm(mpad('--mpad-l')) });
}

/** La hoja que pinta el editor (ancho del body, alto mínimo de la hoja más sus bandas), en mm. */
export function hojaDelEditor(body: HTMLElement | null | undefined, win: Window | null | undefined): HojaEditor | null {
  try {
    const pg = hojasDelDocumento(body)[0];
    if (!body || !pg || !win) return null;
    const csBody = win.getComputedStyle(body);
    const ancho = parseFloat(csBody.width);
    const banda = (v: string) => parseFloat(csBody.getPropertyValue(v)) || 0;
    const alto = parseFloat(win.getComputedStyle(pg).minHeight) + banda('--hf-top') + banda('--hf-bot');
    if (!Number.isFinite(ancho) || !Number.isFinite(alto) || ancho < 100 || alto < 100) return null;
    return { anchoMm: r2(pxAMm(ancho)), altoMm: r2(pxAMm(alto)) };
  } catch {
    return null;
  }
}

/** Relleno y bandas de una hoja medidos en el editor; null si la hoja no tiene estilo calculado. */
export function cajaDeHoja(pg: HTMLElement, win: Window): CajaHoja | null {
  const cs = win.getComputedStyle(pg);
  const csBody = win.getComputedStyle(pg.ownerDocument.body);
  const n = (v: string) => parseFloat(v) || 0;
  const caja = {
    padTop: n(cs.paddingTop),
    padRight: n(cs.paddingRight),
    padBottom: n(cs.paddingBottom),
    padLeft: n(cs.paddingLeft),
    bandaSup: n(csBody.getPropertyValue('--hf-top')),
    bandaInf: n(csBody.getPropertyValue('--hf-bot')),
  };
  // Sin el content_style del editor (hoja aún sin pintar) todo sale a 0: no es un margen.
  return caja.padTop || caja.padRight || caja.padBottom || caja.padLeft ? caja : null;
}

// ── CSS del editor ──────────────────────────────────────────────────────────

/**
 * Reglas del content_style. La hoja con `data-ez-margenes` usa sus --mpad en
 * línea; la banda de encabezado o pie se descuenta del margen (vive dentro de
 * él, como en Word y en el export). Con el margen de la cuenta en el body
 * (ponerMargenCuenta), lo mismo en las hojas que no traen márgenes propios ni
 * plantilla, también las del formato mixto (como el Word: la cuenta manda
 * sobre los márgenes de su plantilla). Sin las marcas, no casan con nada.
 */
export function cssMargenesEditor(): string {
  const regla = (sel: string, t: string, r: string, b: string, l: string) => `
      body.mce-content-body${sel} {
        padding: max(0px, calc(${t} - var(--hf-top, 0px))) ${r} max(0px, calc(${b} - var(--hf-bot, 0px))) ${l} !important;
      }`;
  let css = regla(` .manuscript-page[${ATRIBUTO_MARGENES}]:not([${MARCA_PLANTILLA}])`, 'var(--mpad-t, 38px)', 'var(--mpad-r, 40px)', 'var(--mpad-b, 38px)', 'var(--mpad-l, 40px)');
  css += regla(`[${ATRIBUTO_MARGEN_CUENTA}] > .manuscript-page:not([${ATRIBUTO_MARGENES}]):not([${MARCA_PLANTILLA}])`, 'var(--ez-def-t, 38px)', 'var(--ez-def-r, 40px)', 'var(--ez-def-b, 38px)', 'var(--ez-def-l, 40px)');
  // El encabezado y el pie del documento van al ancho del texto, como en el Word
  // (viven dentro de los márgenes laterales de la sección). Son hermanos de las
  // hojas: sus px y la marca los pone sincronizarBandas en el body. El :not(#…)
  // sube la especificidad por encima de la regla de respaldo del editor para lo
  // que no es hoja (`> :not(.manuscript-page):not(…)…`, 0,7,1), que les fija 40 px.
  const banda = (clase: string) => `body.mce-content-body[${ATRIBUTO_BANDAS}] > .${clase}:not(#ez-margenes)`;
  css += `
      ${banda('doc-header')}, ${banda('doc-footer')}, ${banda('hf-ghost')} {
        padding-left: var(--ez-banda-l, 40px) !important;
        padding-right: var(--ez-banda-r, 40px) !important;
      }`;
  return css;
}

// ── Hojas que no caben ──────────────────────────────────────────────────────

/**
 * La regla del Word para una hoja de prosa que no cabe (compactManuscriptPerPage
 * en processing-ms): encoge la letra hasta que la hoja ocupe el 94 % de su alto
 * útil, como mucho hasta el 68 %; lo que ni así cabe pasa a la hoja siguiente.
 * Con estos números el aviso del editor cuenta lo que va a pasar al exportar.
 */
export const TECHO_EXPORT = 0.94;
export const SUELO_LETRA_EXPORT = 0.68;

/** Medidas de una hoja en px de layout. */
export interface MedidaHoja {
  /** offsetHeight: crece si el contenido no cabe en la hoja. */
  alto: number;
  /** min-height: el papel. */
  minimo: number;
  /** Rellenos y bordes verticales. */
  vertical: number;
}

export type MedirHoja = (pg: HTMLElement) => MedidaHoja | null;

const medirHojaDom = (win: Window): MedirHoja => (pg) => {
  const cs = win.getComputedStyle(pg);
  const n = (v: string) => parseFloat(v) || 0;
  const minimo = n(cs.minHeight);
  if (!(minimo > 0)) return null;
  return { alto: pg.offsetHeight, minimo, vertical: n(cs.paddingTop) + n(cs.paddingBottom) + n(cs.borderTopWidth) + n(cs.borderBottomWidth) };
};

export interface HojaQueNoCabe {
  /** Número de la hoja en el editor (desde 1). */
  hoja: number;
  /** Escala de la letra que necesitaría para caber (como el Word, con el 94 %). */
  escala: number;
  /** Hojas que ocupará al exportar: 1 si basta con reducir la letra. */
  hojas: number;
}

export interface Desborde {
  noCaben: HojaQueNoCabe[];
  /** La letra más pequeña que tocará (entre 0,68 y 1). */
  escalaMinima: number;
  /** Hojas del documento al exportar. */
  hojasTotales: number;
  /** Todas caben reduciendo la letra (ninguna añade hojas). */
  soloReduce: boolean;
}

/** Margen (px) para no avisar por un redondeo: una hoja «se sale» si crece más que esto. */
const HOLGURA_PX = 2;
/** Lo que una hoja que ya se salía puede crecer sin avisar, en fracción del papel. */
const HOLGURA_YA_SALIA = 0.02;
/** Las hojas que añade el reflujo de la Opción 2 (MARCA_HOJA_EXTRA de reflujo-hojas). */
const MARCA_EXTRA = 'data-ez-hoja-extra';

const medirSinFallo = (medir: MedirHoja, pg: HTMLElement): MedidaHoja | null => {
  try { return medir(pg); } catch { return null; }
};

/**
 * Cuánto se sale del papel (px; 0 si cabe) cada hoja propia del documento, en
 * orden y sin las hojas extra del reflujo: la base con la que se comparan los
 * cambios de márgenes. Hay hojas que ya se salen al abrir sin que nadie tocara
 * nada: la transcripción de un PDF digital conserva el paso de los renglones
 * del original y todo el texto de la hoja va en un solo bloque que el reflujo
 * no parte.
 */
export function excesosDeHojas(body: Element | null | undefined, medir: MedirHoja): number[] {
  return hojasDelDocumento(body)
    .filter((pg) => pg.getAttribute(MARCA_EXTRA) !== '1')
    .map((pg) => {
      const m = medirSinFallo(medir, pg);
      return m ? Math.max(0, m.alto - m.minimo) : 0;
    });
}

/**
 * Las hojas cuyo contenido se sale del papel en el editor (la hoja creció más
 * que su alto mínimo): las que el reflujo no reparte (imágenes ancladas,
 * posiciones absolutas, formato mixto…). Con `base` (excesosDeHojas de antes
 * del cambio), una hoja que ya se salía solo cuenta si se sale bastante más.
 * null si todo cabe.
 */
export function desbordeDeHojas(body: Element | null | undefined, medir: MedirHoja, base: readonly number[] | null = null): Desborde | null {
  const hojas = hojasDelDocumento(body);
  const noCaben: HojaQueNoCabe[] = [];
  let hojasTotales = 0;
  let propia = 0;
  hojas.forEach((pg, i) => {
    let previo = 0;
    if (pg.getAttribute(MARCA_EXTRA) !== '1') {
      previo = base?.[propia] ?? 0;
      propia += 1;
    }
    const m = medirSinFallo(medir, pg);
    const holgura = previo > HOLGURA_PX && m ? previo + Math.max(HOLGURA_PX, HOLGURA_YA_SALIA * m.minimo) : HOLGURA_PX;
    if (!m || m.alto <= m.minimo + holgura) {
      hojasTotales += 1;
      return;
    }
    const util = Math.max(1, m.minimo - m.vertical);
    const contenido = Math.max(util, m.alto - m.vertical);
    const escala = Math.min(1, (TECHO_EXPORT * util) / contenido);
    const hojasDeEsta = escala >= SUELO_LETRA_EXPORT ? 1 : Math.ceil(contenido / ((TECHO_EXPORT * util) / SUELO_LETRA_EXPORT));
    noCaben.push({ hoja: i + 1, escala: Math.round(escala * 100) / 100, hojas: hojasDeEsta });
    hojasTotales += hojasDeEsta;
  });
  if (!noCaben.length) return null;
  return {
    noCaben,
    escalaMinima: Math.max(SUELO_LETRA_EXPORT, Math.min(...noCaben.map((h) => h.escala))),
    hojasTotales,
    soloReduce: noCaben.every((h) => h.hojas === 1),
  };
}

const interpolar = (a: Margenes, b: Margenes, t: number): Margenes => ({
  sup: a.sup + (b.sup - a.sup) * t,
  der: a.der + (b.der - a.der) * t,
  inf: a.inf + (b.inf - a.inf) * t,
  izq: a.izq + (b.izq - a.izq) * t,
});
const minimoDe = (a: Margenes, b: Margenes): Margenes => ({ sup: Math.min(a.sup, b.sup), der: Math.min(a.der, b.der), inf: Math.min(a.inf, b.inf), izq: Math.min(a.izq, b.izq) });
const redondear1 = (m: Margenes): Margenes => ({
  sup: Math.floor(m.sup * 10) / 10, der: Math.floor(m.der * 10) / 10, inf: Math.floor(m.inf * 10) / 10, izq: Math.floor(m.izq * 10) / 10,
});

/**
 * «Ajustar»: los márgenes más grandes con los que todas las hojas caben,
 * bajando lo justo desde los elegidos hacia los de antes y, si no basta, hacia
 * el mínimo (5 mm). Prueba en el DOM con las variables en línea de cada hoja y
 * deja cada hoja exactamente como estaba (el style y el data-mce-style). null
 * si ni con el mínimo cabe. Con `base`, «caber» es lo de desbordeDeHojas: lo
 * que ya se salía antes del cambio no obliga a bajar más.
 */
export function buscarAjuste(body: Element | null | undefined, medir: MedirHoja, elegidos: Margenes, anteriores: Margenes | null, base: readonly number[] | null = null): Margenes | null {
  const hojas = hojasDelDocumento(body);
  if (!hojas.length) return null;
  const piso = minimoDe(elegidos, anteriores ?? elegidos);
  const minimo: Margenes = { sup: MARGEN_MIN_MM, der: MARGEN_MIN_MM, inf: MARGEN_MIN_MM, izq: MARGEN_MIN_MM };
  // t de 0 a 1: de los elegidos a los de antes; de 1 a 2: de ahí al mínimo.
  const candidato = (t: number) => redondear1(t <= 1 ? interpolar(elegidos, piso, t) : interpolar(piso, minimoDe(piso, minimo), t - 1));
  const estilos = hojas.map((pg) => pg.getAttribute('style'));
  const caben = (m: Margenes): boolean => {
    for (const pg of hojas) {
      for (const [v, k] of [['--mpad-t', 'sup'], ['--mpad-r', 'der'], ['--mpad-b', 'inf'], ['--mpad-l', 'izq']] as const) pg.style.setProperty(v, pxEnLinea(m[k]));
    }
    return !desbordeDeHojas(body, medir, base);
  };
  try {
    if (!caben(candidato(2))) return null;
    let lo = 0;
    let hi = 2;
    for (let i = 0; i < 12; i++) {
      const t = (lo + hi) / 2;
      if (caben(candidato(t))) hi = t;
      else lo = t;
    }
    return candidato(hi);
  } catch {
    return null;
  } finally {
    hojas.forEach((pg, i) => {
      const e = estilos[i];
      if (e === null) pg.removeAttribute('style');
      else pg.setAttribute('style', e);
    });
  }
}

// ── Botón de TinyMCE ────────────────────────────────────────────────────────

/** Página con el recuadro de los márgenes punteado. */
const ICONO_MARGENES = '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm0 1.5a.5.5 0 0 0-.5.5v16c0 .28.22.5.5.5h12a.5.5 0 0 0 .5-.5V4a.5.5 0 0 0-.5-.5H6Z"/><path d="M8 6h1.2v1.2H8zm2.4 0h1.2v1.2h-1.2zm2.4 0H14v1.2h-1.2zm2.4 0H16v1.2h-.8zM8 16.8h1.2V18H8zm2.4 0h1.2V18h-1.2zm2.4 0H14V18h-1.2zm2.4 0H16V18h-.8zM8 8.4h1.2v1.2H8zm0 2.4h1.2V12H8zm0 2.4h1.2v1.2H8zm0 2.4h1.2v.6H8zM14.8 8.4H16v1.2h-1.2zm0 2.4H16V12h-1.2zm0 2.4H16v1.2h-1.2zm0 2.4H16v.6h-1.2z"/></svg>';

export interface PeticionPersonalizado {
  actuales: Margenes;
  unidad: UnidadMargen;
  hoja: HojaEditor | null;
  /** Aplica a todas las hojas, en un paso de deshacer. Devuelve cuántas cambiaron. */
  aplicar: (m: Margenes) => number;
}

export interface OpcionesBotonMargenes {
  locale?: string | null;
  /** Abreviaturas de las unidades en el idioma de la interfaz («mm», «cm», «in», «po»…). */
  unidades?: { mm: string; in: string; cm?: string } | null;
  /** La unidad del menú y del diálogo para esta hoja (la elegida con «in | cm»); sin ella, mm o in según la hoja. */
  unidad?: (hoja: HojaEditor | null) => UnidadMargen;
  /** «Personalizado…»: abre el diálogo de EZDocu. */
  abrirPersonalizado?: (p: PeticionPersonalizado) => void;
  /** Margen por defecto de la cuenta (Ajustes → Documento traducido); sin él, el de hoy. Se lee en cada uso. */
  porDefecto?: () => Margenes | null;
  /** Tras aplicar (p. ej. volver a medir los rellenos de guiones, que dependen del ancho). */
  despues?: () => void;
  /**
   * Tras cambiar los márgenes: las hojas que no caben y cómo saldrán al
   * exportar, con «ajustar» si bajando los márgenes caben (null: todo cabe).
   */
  onDesborde?: (aviso: AvisoDesborde | null) => void;
  /** «Mostrar regla» al final del menú (regla-margenes): si se ve y cómo alternarla. */
  regla?: { visible: () => boolean; alternar: () => void } | null;
}

/** Lo que el registro deja para la regla de márgenes y el diálogo. */
export interface ApiMargenes {
  /** Aplica a todas las hojas en un paso de deshacer, como el menú (cuenta para el aviso). Devuelve cuántas cambiaron. */
  aplicar: (m: Margenes) => number;
  /** Los márgenes de ahora: los del documento, si no los de la cuenta, si no lo que se ve. */
  actuales: () => Margenes | null;
  /** La hoja que pinta el editor, en mm. */
  hoja: () => HojaEditor | null;
}

export interface AvisoDesborde extends Desborde {
  /** Baja los márgenes lo justo para que todo quepa (un paso de deshacer); null si ni con el mínimo cabe. */
  ajustar: (() => boolean) | null;
}

/** Lo que tarda en asentarse el reflujo antes de mirar qué no cabe. */
export const ESPERA_DESBORDE_MS = 450;

/** Los cambios de márgenes del usuario sobre el documento cargado en el editor. */
interface SesionMargenes {
  /** data-ez-margenes de la primera hoja antes del primer cambio; undefined mientras no haya cambios. */
  inicial: string | null | undefined;
  /** Los márgenes que el usuario eligió (los de la primera hoja tras cada cambio). */
  elegidos: Set<string | null>;
  /** Los últimos vistos: si deshacer o rehacer no los cambia, era otra cosa. */
  vistos: string | null;
  /** excesosDeHojas antes del primer cambio. */
  base: number[] | null;
}

const nuevaSesionMargenes = (vistos: string | null): SesionMargenes => ({ inicial: undefined, elegidos: new Set(), vistos, base: null });

/**
 * Los laterales de las hojas, en el body (no se guarda), para el encabezado y
 * el pie del documento: los del documento, si no los de la cuenta; sin
 * ninguno quedan como siempre (40 px).
 */
export function sincronizarBandas(body: HTMLElement | null | undefined, porDefecto: Margenes | null = null): void {
  if (!body?.style) return;
  const hojas = hojasDelDocumento(body);
  const m = leerMargenes(hojas[0]?.getAttribute(ATRIBUTO_MARGENES)) ?? (hojas.length ? porDefecto : null);
  if (m) {
    body.style.setProperty('--ez-banda-l', pxEnLinea(m.izq));
    body.style.setProperty('--ez-banda-r', pxEnLinea(m.der));
    body.setAttribute(ATRIBUTO_BANDAS, '1');
  } else {
    body.style.removeProperty('--ez-banda-l');
    body.style.removeProperty('--ez-banda-r');
    body.removeAttribute(ATRIBUTO_BANDAS);
  }
}

const margenValido = (m: Margenes | null | undefined): m is Margenes => !!m
  && CAMPOS_MARGEN.every((k) => Number.isFinite(m[k]) && m[k] >= 0 && m[k] <= 200);

/**
 * La preferencia de márgenes de la cuenta (`margenesTraduccion` de
 * lib/preferencias-documento) en mm; null = «los del original».
 */
export function margenesDePreferencia(
  p: { modo?: unknown; arriba?: unknown; abajo?: unknown; izquierda?: unknown; derecha?: unknown } | null | undefined,
): Margenes | null {
  if (!p || p.modo !== 'personalizado') return null;
  const n = (v: unknown) => (typeof v === 'number' ? v : NaN);
  const m = { sup: n(p.arriba), der: n(p.derecha), inf: n(p.abajo), izq: n(p.izquierda) };
  return margenValido(m) ? m : null;
}

/**
 * El margen por defecto de la cuenta en el body del editor (variables y
 * atributo que no se guardan): lo usan las hojas sin márgenes propios
 * (cssMargenesEditor). null lo quita y las hojas vuelven a lo de hoy.
 */
export function ponerMargenCuenta(body: HTMLElement | null | undefined, m: Margenes | null): void {
  if (!body?.style) return;
  const lados: Array<[string, keyof Margenes]> = [['--ez-def-t', 'sup'], ['--ez-def-r', 'der'], ['--ez-def-b', 'inf'], ['--ez-def-l', 'izq']];
  if (margenValido(m)) {
    for (const [v, k] of lados) body.style.setProperty(v, pxEnLinea(m[k]));
    body.setAttribute(ATRIBUTO_MARGEN_CUENTA, textoMargenes(m));
  } else {
    for (const [v] of lados) body.style.removeProperty(v);
    body.removeAttribute(ATRIBUTO_MARGEN_CUENTA);
  }
}

/** El editor ya montado toma (o suelta) el margen de la cuenta en vivo: hojas, bandas y sus réplicas. */
export function refrescarMargenCuenta(
  editor: { getBody?: () => HTMLElement | null | undefined; dispatch?: (nombre: string, args?: unknown) => unknown; removed?: boolean } | null | undefined,
  m: Margenes | null,
): void {
  try {
    if (!editor || editor.removed) return;
    const body = editor.getBody?.();
    if (!body) return;
    ponerMargenCuenta(body, m);
    sincronizarBandas(body, m);
    // Las bandas cambian de ancho (y quizá de alto): sus réplicas se vuelven a medir.
    if (body.querySelector('.doc-header, .doc-footer')) editor.dispatch?.('EzHojasCambiadas');
  } catch {
    /* sin el margen de la cuenta, el editor sigue con el de hoy */
  }
}

/** Lo mínimo del editor de TinyMCE que usa el botón. */
export interface EditorConMargenes {
  getBody: () => HTMLElement;
  on?: (eventos: string, fn: (e?: { format?: string; selection?: boolean }) => void, prepend?: boolean) => unknown;
  getWin?: () => Window;
  dom: DomMargenes;
  undoManager: { transact: (fn: () => void) => unknown };
  nodeChanged?: () => void;
  dispatch?: (nombre: string, args?: unknown) => unknown;
  translate?: (texto: string) => string;
  mode?: { isReadOnly?: () => boolean };
  ui: { registry: {
    addIcon: (nombre: string, svg: string) => void;
    addMenuButton: (nombre: string, spec: Record<string, unknown>) => void;
  } };
}

/**
 * Registra el botón «Márgenes»: Como el original (si hay medida), Normal,
 * Estrecho, Moderado, Ancho y Personalizado… Cada elección es UN paso de
 * deshacer, y el reflujo de la Opción 2 (reflujo-hojas) mide las hojas antes
 * y las reacomoda dentro de ese mismo paso, con los eventos de «Aplicar a todo».
 */
export function registrarBotonMargenes(editor: EditorConMargenes, opciones: OpcionesBotonMargenes = {}): ApiMargenes {
  const traducir = (t: string) => {
    try { return editor.translate?.(t) || t; } catch { return t; }
  };
  const ventana = () => {
    try { return editor.getWin?.() ?? null; } catch { return null; }
  };
  const porDefecto = () => {
    try { return opciones.porDefecto?.() ?? null; } catch { return null; }
  };
  const actuales = (): Margenes | null => {
    const win = ventana();
    return margenesActuales(editor.getBody(), { porDefecto: porDefecto(), caja: win ? (pg) => cajaDeHoja(pg, win) : undefined });
  };
  const soloLectura = () => {
    try { return !!editor.mode?.isReadOnly?.(); } catch { return false; }
  };
  const marcaActual = (): string | null => {
    try { return hojasDelDocumento(editor.getBody())[0]?.getAttribute(ATRIBUTO_MARGENES) ?? null; } catch { return null; }
  };
  const medirHoja: MedirHoja = (pg) => {
    const win = ventana();
    return win ? medirHojaDom(win)(pg) : null;
  };
  // El aviso de «no cabe» es de los cambios de márgenes del usuario en esta
  // sesión del documento: justo después de elegirlos (menú, diálogo o
  // «Ajustar») o de deshacer o rehacer ese cambio. Nunca al abrir, al
  // autoguardar, por el margen de la cuenta ni por los márgenes con que llegó
  // el documento; y lo que ya se salía antes del primer cambio no se avisa
  // salvo que el cambio lo empeore.
  let sesion = nuevaSesionMargenes(null);
  // Cada mirada pendiente lleva su turno: callar o cargar otro documento la anula.
  let turno = 0;
  const avisar = (aviso: AvisoDesborde | null) => {
    try { opciones.onDesborde?.(aviso); } catch { /* sin aviso */ }
  };
  const callar = () => {
    turno += 1;
    avisar(null);
  };
  /** Mira, ya con el reflujo asentado, qué hojas no caben con los márgenes elegidos. */
  const evaluarDesborde = (elegidos: Margenes | null, anteriores: Margenes | null) => {
    if (!opciones.onDesborde) return;
    turno += 1;
    const mio = turno;
    const deEstaSesion = sesion;
    setTimeout(() => {
      if (mio !== turno || deEstaSesion !== sesion) return;
      try {
        const body = editor.getBody();
        const d = desbordeDeHojas(body, medirHoja, sesion.base);
        if (!d) {
          avisar(null);
          return;
        }
        const desde = elegidos ?? actuales();
        const ajuste = desde ? buscarAjuste(body, medirHoja, desde, anteriores, sesion.base) : null;
        avisar({ ...d, ajustar: ajuste ? () => aplicar(ajuste) > 0 : null });
      } catch {
        avisar(null);
      }
    }, ESPERA_DESBORDE_MS);
  };
  const aplicar = (m: Margenes): number => {
    if (soloLectura()) return 0;
    let n = 0;
    let anteriores: Margenes | null = null;
    try { anteriores = actuales(); } catch { anteriores = null; }
    if (sesion.inicial === undefined) {
      // Primer cambio del usuario: cómo estaba el documento antes de tocarlo.
      sesion.inicial = marcaActual();
      try { sesion.base = excesosDeHojas(editor.getBody(), medirHoja); } catch { sesion.base = null; }
    }
    try {
      const limpios = redondearMargenes(m);
      editor.undoManager.transact(() => {
        editor.dispatch?.('EzTodoElDocumentoAntes');
        const body = editor.getBody();
        n = aplicarMargenes(body, limpios, editor.dom);
        if (n) {
          // Las bandas toman el ancho nuevo (pueden ganar un renglón) y se
          // vuelven a medir antes del reflujo: el alto útil de cada hoja depende de ellas.
          sincronizarBandas(body, porDefecto());
          if (body.querySelector('.doc-header, .doc-footer')) editor.dispatch?.('EzHojasCambiadas');
          editor.dispatch?.('EzTodoElDocumento');
        }
      });
      editor.nodeChanged?.();
      if (n) {
        editor.dispatch?.('EzMargenesCambiados', { margenes: limpios });
        opciones.despues?.();
        sesion.vistos = marcaActual();
        sesion.elegidos.add(sesion.vistos);
        evaluarDesborde(limpios, anteriores);
      }
    } catch {
      /* un fallo aquí no debe tumbar el editor (transact no revierte lo ya aplicado) */
    }
    return n;
  };
  // Otro documento en el editor (al montarlo, al cargarlo o al sustituirlo):
  // sesión nueva y sin aviso. Deshacer y rehacer también pasan por SetContent,
  // pero en «raw»; pegar o insertar llevan `selection`.
  const sesionNueva = () => {
    sesion = nuevaSesionMargenes(marcaActual());
    callar();
  };
  editor.on?.('init LoadContent', sesionNueva);
  editor.on?.('SetContent', (e) => {
    if (e?.format === 'raw' || e?.selection) return;
    sesionNueva();
  });
  // Deshacer o rehacer un cambio de márgenes de esta sesión vuelve a mirar;
  // volver a como estaba el documento quita el aviso; deshacer lo escrito no
  // cambia nada.
  editor.on?.('Undo Redo', () => {
    const m = marcaActual();
    if (m === sesion.vistos) return;
    sesion.vistos = m;
    if (sesion.inicial !== undefined && m !== sesion.inicial && sesion.elegidos.has(m)) evaluarDesborde(null, null);
    else callar();
  });
  // Cargar, deshacer o rehacer trae los márgenes del HTML: el margen de la
  // cuenta (para las hojas sin márgenes propios) y las bandas se ponen antes de
  // que las réplicas del encabezado y el pie se midan (prepend).
  editor.on?.('init SetContent Undo Redo', () => {
    try {
      const body = editor.getBody();
      const d = porDefecto();
      ponerMargenCuenta(body, d);
      sincronizarBandas(body, d);
    } catch { /* las hojas y las bandas se quedan como hoy */ }
  }, true);
  editor.ui.registry.addIcon('ez-margenes', ICONO_MARGENES);
  editor.ui.registry.addMenuButton(BOTON_MARGENES, {
    icon: 'ez-margenes',
    tooltip: TEXTO_BOTON,
    fetch: (callback: (items: unknown[]) => void) => {
      const body = editor.getBody();
      const hoja = hojaDelEditor(body, ventana());
      let unidad: UnidadMargen = unidadDeHoja(hoja);
      try { unidad = opciones.unidad?.(hoja) ?? unidad; } catch { /* la de la hoja */ }
      let ahora: Margenes | null = null;
      try { ahora = actuales(); } catch { /* sin opción marcada */ }
      const item = (texto: string, m: Margenes) => ({
        type: 'togglemenuitem',
        text: `${traducir(texto)} (${resumenMargenes(m, unidad, opciones.locale, opciones.unidades)})`,
        active: margenesIguales(ahora, m),
        onAction: () => aplicar(m),
      });
      const original = margenesDelOriginal(body);
      const regla = opciones.regla;
      let reglaVisible = false;
      try { reglaVisible = !!regla?.visible(); } catch { /* como oculta */ }
      callback([
        ...(original ? [item(TEXTO_ORIGINAL, original)] : []),
        ...PRESETS_MARGENES.map((p) => item(p.titulo, p.margenes)),
        { type: 'separator' },
        {
          type: 'menuitem',
          text: TEXTO_PERSONALIZADO,
          onAction: () => {
            const base = ahora ?? PRESETS_MARGENES[0].margenes;
            opciones.abrirPersonalizado?.({ actuales: base, unidad, hoja, aplicar });
          },
        },
        ...(regla ? [
          { type: 'separator' },
          {
            type: 'togglemenuitem',
            text: traducir(TEXTO_MOSTRAR_REGLA),
            active: reglaVisible,
            onAction: () => { try { regla.alternar(); } catch { /* la regla se queda como estaba */ } },
          },
        ] : []),
      ]);
    },
  });
  const hoja = (): HojaEditor | null => {
    try { return hojaDelEditor(editor.getBody(), ventana()); } catch { return null; }
  };
  const actualesSinFallo = (): Margenes | null => {
    try { return actuales(); } catch { return null; }
  };
  return { aplicar, actuales: actualesSinFallo, hoja };
}

/**
 * La traducción no garantiza que el modelo copie los atributos de cada hoja.
 * Si las hojas del origen llevan todas los mismos márgenes y la traducción no
 * trae ninguno, devuelve esos márgenes para ponérselos; si no, null.
 */
export function margenesParaHeredar(origen: Element | null | undefined, destino: Element | null | undefined): Margenes | null {
  const hojasOrigen = hojasDelDocumento(origen);
  const hojasDestino = hojasDelDocumento(destino);
  if (!hojasOrigen.length || !hojasDestino.length) return null;
  if (hojasDestino.some((pg) => pg.hasAttribute(ATRIBUTO_MARGENES))) return null;
  const valores = new Set(hojasOrigen.map((pg) => pg.getAttribute(ATRIBUTO_MARGENES) || ''));
  if (valores.size !== 1) return null;
  return leerMargenes(Array.from(valores)[0]);
}

/** Lo que usa heredarMargenes del editor de TinyMCE (el de la traducción). */
export interface EditorHerencia {
  getBody: () => HTMLElement | null | undefined;
  dom: DomMargenes;
  removed?: boolean;
  undoManager?: { ignore?: (fn: () => void) => void; clear?: () => void; add?: () => unknown };
  setDirty?: (v: boolean) => void;
  dispatch?: (nombre: string, args?: unknown) => unknown;
  mode?: { isReadOnly?: () => boolean };
}

/**
 * Al abrir la traducción: si en Revisión se pusieron márgenes (iguales en todas
 * las hojas) y la traducción llega sin ninguno, se le ponen. Es como venía el
 * documento, no un paso de deshacer: la historia empieza con ellos y el
 * reflujo de la Opción 2 los reacomoda en ese primer nivel. Devuelve cuántas
 * hojas cambió; ante cualquier fallo, 0 y la traducción como estaba.
 */
export function heredarMargenes(editor: EditorHerencia, origenHtml: string | null | undefined, parsear: (html: string) => Element | null): number {
  try {
    if (!origenHtml || !origenHtml.includes(ATRIBUTO_MARGENES) || editor.removed || editor.mode?.isReadOnly?.()) return 0;
    const body = editor.getBody();
    if (!body) return 0;
    const m = margenesParaHeredar(parsear(origenHtml), body);
    if (!m) return 0;
    let n = 0;
    const poner = () => {
      editor.dispatch?.('EzTodoElDocumentoAntes');
      n = aplicarMargenes(body, m, editor.dom);
      if (n) {
        sincronizarBandas(body);
        editor.dispatch?.('EzTodoElDocumento');
      }
    };
    if (editor.undoManager?.ignore) editor.undoManager.ignore(poner);
    else poner();
    if (!n) return 0;
    editor.undoManager?.clear?.();
    editor.undoManager?.add?.();
    editor.setDirty?.(true);
    editor.dispatch?.('change');
    return n;
  } catch {
    return 0;
  }
}
